import React, { useState, useEffect, useMemo } from 'react';
import { Drawer, Collapse, List, Typography, Tag, Empty, Spin, App, Input, Button } from 'antd';
import { ClockCircleOutlined, DollarOutlined, ShoppingCartOutlined, SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { fetchUserResults, type HistoryGroup } from '../api/client';
import { useAuthStore } from '../stores/authStore';

interface HistoryProps {
  open: boolean;
  onClose: () => void;
  // number → запись по DB id (клик в истории); string → share-токен (ручной ввод).
  onLoadResult: (idOrShare: number | string) => void;
}

const History: React.FC<HistoryProps> = ({ open, onClose, onLoadResult }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { isAdmin } = useAuthStore();
  const [groups, setGroups] = useState<HistoryGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setLoading(true);
    fetchUserResults()
      .then((res) => setGroups(res.groups))
      .catch(() => message.error(t('idea_calc.history.msg_load_error')))
      .finally(() => setLoading(false));
  }, [open]);

  const query = search.trim().replace(/\.json$/i, '').toLowerCase();

  // В реальной базе тысячи расчётов — рендерим не больше MAX_RENDERED_GROUPS
  // групп за раз, иначе Collapse кладёт в DOM тысячи узлов и панель тормозит.
  const MAX_RENDERED_GROUPS = 100;

  const filteredGroups = useMemo(() => {
    if (!query) return groups;
    return groups
      .map((group) => {
        if (group.asin.toLowerCase().includes(query)) return group;
        const versions = group.versions.filter((v) => v.fileName.toLowerCase().includes(query));
        if (versions.length === 0) return null;
        return { ...group, versions, versions_count: versions.length };
      })
      .filter((g): g is HistoryGroup => g !== null);
  }, [groups, query]);

  const visibleGroups = filteredGroups.slice(0, MAX_RENDERED_GROUPS);
  const hiddenCount = filteredGroups.length - visibleGroups.length;

  const handleLoad = (id: number) => {
    onLoadResult(id);
    onClose();
  };

  // Enter в поле, когда фильтр ничего не нашёл: сохраняем прежнее поведение
  // поля «load by id» — число → DB id, иначе строка = share-токен.
  const handleLoadByInput = () => {
    const raw = search.trim().replace(/\.json$/i, '');
    if (!raw || filteredGroups.length > 0) return;
    const num = Number(raw);
    const idOrShare = Number.isInteger(num) && num > 0 ? num : raw;
    onLoadResult(idOrShare);
    setSearch('');
    onClose();
  };

  const formatDate = (ts: string) => {
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return ts;
    }
  };

  return (
    <Drawer
      title={
        <span>
          {t('idea_calc.history.title')}
          {isAdmin && <Tag color="red" style={{ marginLeft: 8 }}>Admin</Tag>}
        </span>
      }
      placement="right"
      styles={{ wrapper: { width: 480 } }}
      open={open}
      onClose={onClose}
    >
      <div style={{ marginBottom: 16 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
          {t('idea_calc.history.search_label')}
        </Typography.Text>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t('idea_calc.history.search_placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={handleLoadByInput}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : groups.length === 0 ? (
        <Empty description={t('idea_calc.history.empty_state')} />
      ) : filteredGroups.length === 0 ? (
        <Empty description={t('idea_calc.history.no_matches')}>
          <Button type="primary" ghost icon={<DownloadOutlined />} onClick={handleLoadByInput}>
            {t('idea_calc.history.load_as_input')}
          </Button>
        </Empty>
      ) : (
        <>
        <Collapse
          accordion={!query}
          {...(query ? { activeKey: visibleGroups.map((g) => g.asin) } : {})}
          items={visibleGroups.map((group) => ({
            key: group.asin,
            label: (
              <span>
                <Typography.Text strong>{group.asin}</Typography.Text>
                <Tag style={{ marginLeft: 8 }}>{t('idea_calc.history.versions_count', { count: group.versions_count })}</Tag>
              </span>
            ),
            children: (
              <List
                size="small"
                dataSource={group.versions}
                renderItem={(v) => (
                  <List.Item
                    actions={[
                      <Button
                        key="load"
                        size="small"
                        type="primary"
                        ghost
                        icon={<DownloadOutlined />}
                        onClick={() => handleLoad(v.id)}
                      >
                        {t('idea_calc.history.load_button')}
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={v.fileName}
                      description={
                        <span style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                          <span><ClockCircleOutlined /> {formatDate(v.timestamp)}</span>
                          {v.selling_price != null && (
                            <span><DollarOutlined /> ${v.selling_price}</span>
                          )}
                          {v.monthly_sales != null && (
                            <span><ShoppingCartOutlined /> {t('idea_calc.history.monthly_sales_short', { count: v.monthly_sales })}</span>
                          )}
                        </span>
                      }
                    />
                  </List.Item>
                )}
              />
            ),
          }))}
        />
        {hiddenCount > 0 && (
          <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 12, fontSize: 12 }}>
            {t('idea_calc.history.truncated_hint', { shown: visibleGroups.length, total: filteredGroups.length })}
          </Typography.Text>
        )}
        </>
      )}
    </Drawer>
  );
};

export default History;
